import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { AdminBulkActions } from '../components/AdminBulkActions';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../lib/api';
import { RefreshCw, Building2, Calculator, Trash2, Upload, Loader2 } from 'lucide-react';
import { Pagination } from '../components/Pagination';
import { toast } from 'sonner';
import { bankLabel, requestTheme, allowedBanks } from '../utils/requestTheme';
import { ExportMenu } from '../components/ExportMenu';

const EMPTY_SUMMARY = { selected: { items: [] }, exported: { items: [] }, uploaded: { items: [] } };
// Antrean *_OPS khusus Operasional Payment Request; template export sama.
const BANK_TABS = [
  { value: 'BSI', tab: 'data-[state=active]:bg-emerald-600 data-[state=active]:text-white', btn: 'bg-emerald-600 hover:bg-emerald-700' },
  { value: 'BRI', tab: 'data-[state=active]:bg-blue-600 data-[state=active]:text-white', btn: 'bg-blue-600 hover:bg-blue-700' },
  // Ops: keluarga warna sama dengan BSI/BRI, nada lebih gelap + label OPS.
  { value: 'BSI_OPS', ops: true, tab: 'text-emerald-800 data-[state=active]:bg-emerald-800 data-[state=active]:text-white', btn: 'bg-emerald-800 hover:bg-emerald-900 ring-2 ring-emerald-300' },
  { value: 'BRI_OPS', ops: true, tab: 'text-blue-800 data-[state=active]:bg-blue-800 data-[state=active]:text-white', btn: 'bg-blue-800 hover:bg-blue-900 ring-2 ring-blue-300' },
];

// Label kecil "OPS" pada tombol/tab antrean operasional.
function OpsTag() {
  return <span className="ml-1 rounded px-1 py-px text-[9px] font-bold tracking-wide bg-white/90 text-slate-800 border border-current">OPS</span>;
}

// Penanda IPR/OPR di samping nomor request (invoice & DP tidak diberi penanda).
function RequestCodeBadge({ item }) {
  if (item.source_type !== 'internal') return null;
  const theme = requestTheme(item.category);
  return <span className={`ml-1 px-1 py-0.5 rounded text-[10px] font-bold align-middle ${theme.badge}`}>{theme.code}</span>;
}


export default function BankProcess() {
  const { user } = useAuth();
  // Hanya antrean dalam cakupan approval (Umum: BSI/BRI, Operasional: BSI Ops/BRI Ops).
  const allowed = allowedBanks(user);
  const tabs = BANK_TABS.filter(t => allowed.includes(t.value));
  const [bankData, setBankData] = useState(() => Object.fromEntries(BANK_TABS.map(t => [t.value, EMPTY_SUMMARY])));
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState(() => tabs[0]?.value || 'BSI');
  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [bankPage, setBankPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchFilter.trim()), 400);
    return () => clearTimeout(t);
  }, [searchFilter]);

  useEffect(() => {
    fetchData(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const fetchData = async (page = bankPage) => {
    setLoading(true);
    try {
      const searchParam = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';
      const results = await Promise.all(
        tabs.map(t => api.get(`/bank-process/summary/${t.value}?page=${page}&limit=20${searchParam}`))
      );
      setBankData(prev => ({ ...prev, ...Object.fromEntries(tabs.map((t, i) => [t.value, results[i].data])) }));
      setBankPage(page);
    } catch (error) {
      toast.error('Gagal memuat data proses bank');
    } finally {
      setLoading(false);
    }
  };

  // Admin delete functions
  const openDeleteDialog = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async (reason) => {
    if (!itemToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/admin/transactions/bank_process/${itemToDelete.id}`, {
        data: { reason }
      });
      toast.success(`${itemToDelete.request_number} berhasil dihapus`);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus data');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleUpdateItem = async (itemId, field, value) => {
    try {
      const updateData = { [field]: parseFloat(value) || 0 };
      await api.put(`/bank-process/${itemId}`, updateData);
      toast.success('Data berhasil diupdate');
      fetchData();
    } catch (error) {
      toast.error('Gagal mengupdate data');
    }
  };

  const handleExport = async (bankType, includeExported = false) => {
    setExporting(true);
    try {
      const response = await api.post(
        `/bank-process/export/${bankType}?include_exported=${includeExported}`,
        {},
        { responseType: 'blob' }
      );
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${bankType}_Payment_${new Date().toISOString().split('T')[0]}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match) filename = match[1].replace(/['"]/g, '');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export ${bankLabel(bankType)} berhasil!${includeExported ? ' (Termasuk data yang sudah di-export)' : ' Data dipindahkan ke Upload Bukti.'}`);
      fetchData();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Tidak ada data untuk di-export');
      } else {
        toast.error('Gagal export data');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID').format(value || 0);
  };

  const renderItemsTable = (items, isEditable = true) => {
    const filtered = items || [];
    if (!filtered || filtered.length === 0) {
      return <p className="text-slate-500 text-center py-8">Tidak ada data</p>;
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left">
              <th className="px-3 py-2 font-semibold">No. Request</th>
              <th className="px-3 py-2 font-semibold">Voucher Gab.</th>
              <th className="px-3 py-2 font-semibold">No. PO</th>
              <th className="px-3 py-2 font-semibold">Tipe</th>
              <th className="px-3 py-2 font-semibold">Perusahaan</th>
              <th className="px-3 py-2 font-semibold">Penerima</th>
              <th className="px-3 py-2 font-semibold">Rekening</th>
              <th className="px-3 py-2 font-semibold">Bank</th>
              <th className="px-3 py-2 font-semibold text-right">Nilai Pengajuan</th>
              <th className="px-3 py-2 font-semibold text-right">Sebelum PPN</th>
              <th className="px-3 py-2 font-semibold">Admin Bank</th>
              <th className="px-3 py-2 font-semibold">PPh (%)</th>
              <th className="px-3 py-2 font-semibold text-right">PPh Amount</th>
              <th className="px-3 py-2 font-semibold text-right">Nominal Transaksi</th>
              {user?.role === 'admin' && <th className="px-3 py-2 font-semibold">Aksi</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => (
              <tr key={item.id} className="border-b" data-testid={`bank-item-${item.id}`}>
                <td className="px-3 py-2 font-mono font-semibold whitespace-nowrap">{item.request_number}<RequestCodeBadge item={item} /></td>
                {/* Penanda dilampirkan oleh GET /bank-process lewat join, bukan
                    disalin ke dokumen bank_process — supaya grup yang dilepas
                    langsung hilang dari sini. */}
                <td className="px-3 py-2">
                  {item.voucher_gabungan_label ? (
                    <span
                      className="inline-block px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-800"
                      title={`${item.voucher_gabungan_label} (${item.voucher_gabungan_count} invoice)`}
                      data-testid={`bank-voucher-gabungan-${item.id}`}
                    >
                      GAB {item.voucher_gabungan_count}
                    </span>
                  ) : (
                    <span className="text-slate-400 text-xs">-</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{item.po_number || '-'}</td>
                <td className="px-3 py-2">{item.tipe_request}</td>
                <td className="px-3 py-2 text-sm">{item.company_name || '-'}</td>
                <td className="px-3 py-2">{item.penerima_nama}</td>
                <td className="px-3 py-2 font-mono text-xs">{item.penerima_rekening}</td>
                <td className="px-3 py-2 text-sm">{item.penerima_bank || '-'}</td>
                <td className="px-3 py-2 text-right font-mono">Rp {formatCurrency(item.nilai_pengajuan)}</td>
                <td className="px-3 py-2 text-right font-mono">Rp {formatCurrency(item.nilai_sebelum_ppn)}</td>
                <td className="px-3 py-2">
                  {isEditable ? (
                    <Input
                      type="number"
                      className="w-28 h-8 text-sm font-mono"
                      defaultValue={item.admin_bank || 0}
                      onBlur={(e) => handleUpdateItem(item.id, 'admin_bank', e.target.value)}
                      data-testid={`admin-bank-${item.id}`}
                    />
                  ) : (
                    <span className="font-mono">Rp {formatCurrency(item.admin_bank)}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {isEditable ? (
                    <div className="flex items-center gap-1">
                      <select
                        className="h-8 px-2 text-xs border rounded"
                        defaultValue={item.pph_type || ''}
                        onChange={(e) => handleUpdateItem(item.id, 'pph_type', e.target.value)}
                        data-testid={`pph-type-${item.id}`}
                      >
                        <option value="">-</option>
                        <option value="PPh21">PPh 21</option>
                        <option value="PPh23">PPh 23</option>
                        <option value="PPh4(2)">PPh 4(2)</option>
                      </select>
                      <Input
                        type="number"
                        step="0.01"
                        className="w-16 h-8 text-sm font-mono"
                        defaultValue={item.pph_percentage || 0}
                        onBlur={(e) => handleUpdateItem(item.id, 'pph_percentage', e.target.value)}
                        data-testid={`pph-pct-${item.id}`}
                      />
                      <span className="text-xs">%</span>
                    </div>
                  ) : (
                    <span className="text-sm">{item.pph_type} {item.pph_percentage}%</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono text-rose-600">
                  Rp {formatCurrency(item.pph_amount)}
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-600">
                  Rp {formatCurrency(item.nominal_transaksi)}
                </td>
                {user?.role === 'admin' && (
                  <td className="px-3 py-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => openDeleteDialog(item)}
                      data-testid={`delete-bank-item-${item.id}`}
                      title="Hapus"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-slate-100 font-semibold">
              <td colSpan="7" className="px-3 py-2">TOTAL</td>
              <td className="px-3 py-2 text-right font-mono">
                Rp {formatCurrency(filtered.reduce((sum, i) => sum + (i.nilai_pengajuan || 0), 0))}
              </td>
              <td className="px-3 py-2 text-right font-mono">
                Rp {formatCurrency(filtered.reduce((sum, i) => sum + (i.nilai_sebelum_ppn || 0), 0))}
              </td>
              <td className="px-3 py-2 font-mono">
                Rp {formatCurrency(filtered.reduce((sum, i) => sum + (i.admin_bank || 0), 0))}
              </td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2 text-right font-mono text-rose-600">
                Rp {formatCurrency(filtered.reduce((sum, i) => sum + (i.pph_amount || 0), 0))}
              </td>
              <td className="px-3 py-2 text-right font-mono text-emerald-600">
                Rp {formatCurrency(filtered.reduce((sum, i) => sum + (i.nominal_transaksi || 0), 0))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderBankTab = (bankType, data) => (
    <div className="space-y-6">
      {/* Selected - Ready to Export */}
      <Card className="border-l-4 border-l-amber-500">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calculator className="h-5 w-5 text-amber-600" />
                Siap Proses - {bankLabel(bankType)}
              </CardTitle>
              <p className="text-sm text-slate-500">
                {data.selected?.count || 0} transaksi | Total: Rp {formatCurrency(data.selected?.total_amount)}
              </p>
            </div>
            <div className="flex gap-2">
            <ExportMenu table="bank-process" params={{ bank_type: bankType, status: 'selected', search: debouncedSearch }} label="Export Tabel" testId={`export-table-${bankType.toLowerCase()}-selected`} />
            {data.selected?.items?.length > 0 && (
              <Button
                onClick={() => handleExport(bankType)}
                disabled={exporting}
                className={BANK_TABS.find(t => t.value === bankType)?.btn}
                data-testid={`export-${bankType.toLowerCase()}`}
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Export Excel
              </Button>
            )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {renderItemsTable(data.selected?.items || [], true)}
          <Pagination page={bankPage} totalPages={data.selected?.total_pages || 1} total={data.selected?.count || 0} onPageChange={(p) => fetchData(p)} />
        </CardContent>
      </Card>

      {/* Exported - Moved to Upload */}
      {(data.exported?.items?.length > 0 || data.uploaded?.items?.length > 0) && (
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  Sudah Di-export ({(data.exported?.count || 0) + (data.uploaded?.count || 0)} transaksi)
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Data ini sudah dipindahkan ke menu Upload Bukti Transaksi
                </p>
              </div>
              <div className="flex gap-2">
              <ExportMenu table="bank-process" params={{ bank_type: bankType, status: 'exported', search: debouncedSearch }} label="Export Tabel" testId={`export-table-${bankType.toLowerCase()}-exported`} />
              {data.exported?.items?.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() => handleExport(bankType, true)}
                  disabled={exporting}
                  className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                  data-testid={`re-export-${bankType.toLowerCase()}`}
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Re-Export Excel
                </Button>
              )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="px-3 py-2 font-semibold">No. Request</th>
                    <th className="px-3 py-2 font-semibold">No. PO</th>
                    <th className="px-3 py-2 font-semibold">Tipe</th>
                    <th className="px-3 py-2 font-semibold">Perusahaan</th>
                    <th className="px-3 py-2 font-semibold">Penerima</th>
                    <th className="px-3 py-2 font-semibold">No. Rekening</th>
                    <th className="px-3 py-2 font-semibold">Bank</th>
                    <th className="px-3 py-2 font-semibold text-right">Nominal Transaksi</th>
                    <th className="px-3 py-2 font-semibold">Batch / Row</th>
                    <th className="px-3 py-2 font-semibold">Export Terakhir</th>
                    <th className="px-3 py-2 font-semibold text-center">Jumlah Export</th>
                    {user?.role === 'admin' && <th className="px-3 py-2 font-semibold">Aksi</th>}
                  </tr>
                </thead>
                <tbody>
                  {[...(data.exported?.items || []), ...(data.uploaded?.items || [])].map((item) => (
                    <tr key={item.id} className="border-b">
                      <td className="px-3 py-2 font-mono text-sm font-semibold whitespace-nowrap">{item.request_number}<RequestCodeBadge item={item} /></td>
                      <td className="px-3 py-2 font-mono text-xs">{item.po_number || '-'}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-1 bg-slate-100 rounded text-xs font-semibold">{item.tipe_request}</span>
                      </td>
                      <td className="px-3 py-2 text-sm">{item.company_name || '-'}</td>
                      <td className="px-3 py-2">{item.penerima_nama}</td>
                      <td className="px-3 py-2 font-mono text-xs">{item.penerima_rekening || '-'}</td>
                      <td className="px-3 py-2 text-sm">{item.penerima_bank || '-'}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold text-emerald-600">
                        Rp {formatCurrency(item.nominal_transaksi)}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs" title={item.export_batch_id || ''}>
                        {item.export_batch_label ? (
                          <span>{item.export_batch_label}<br/><span className="text-slate-500">baris #{item.export_row_no ?? '-'}</span></span>
                        ) : (item.export_batch_id ? `${(item.export_batch_id || '').slice(0,8)}… #${item.export_row_no ?? '-'}` : '-')}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">
                        {item.exported_at && new Date(item.exported_at).toLocaleString('id-ID')}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold">
                          {item.export_count || 1}x
                        </span>
                      </td>
                      {user?.role === 'admin' && (
                        <td className="px-3 py-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => openDeleteDialog(item)}
                            data-testid={`delete-exported-${item.id}`}
                            title="Hapus"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="bank-process-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Proses Bank</h1>
            <p className="text-slate-500 mt-1">Ringkasan pembayaran BSI, BRI, BSI Ops & BRI Ops dengan kalkulasi PPh & Admin Bank</p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Cari nomor request, penerima, perusahaan..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="w-72 h-9"
              data-testid="bank-search-filter"
            />
            <Button variant="outline" onClick={fetchData} data-testid="refresh-bank-process">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
            {user?.role === 'admin' && (
              <AdminBulkActions collectionType="bank_process" collectionLabel="Bank Process" onRefresh={fetchData} />
            )}
          </div>
        </div>

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Calculator className="h-5 w-5 text-slate-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-slate-700">Formula Perhitungan:</p>
                <p className="text-slate-600">
                  <strong>PPh Amount</strong> = PPh% × Nilai Sebelum PPN<br/>
                  <strong>Nominal Transaksi</strong> = Nilai Pengajuan - PPh Amount - Admin Bank
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={`grid w-full h-auto ${tabs.length > 2 ? 'max-w-2xl grid-cols-2 md:grid-cols-4' : 'max-w-md grid-cols-2'}`}>
              {tabs.map(t => (
                <TabsTrigger key={t.value} value={t.value} className={t.tab} data-testid={`bank-tab-${t.value.toLowerCase()}`}>
                  <Building2 className="h-4 w-4 mr-2" />
                  {t.ops ? t.value.split('_')[0] : bankLabel(t.value)}
                  {t.ops && <OpsTag />}
                  <span className="ml-1">({bankData[t.value]?.selected?.count || 0})</span>
                </TabsTrigger>
              ))}
            </TabsList>
            {tabs.map(t => (
              <TabsContent key={t.value} value={t.value} className="mt-6">
                {renderBankTab(t.value, bankData[t.value] || EMPTY_SUMMARY)}
              </TabsContent>
            ))}
          </Tabs>
        )}

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDelete}
          title="Hapus Data Bank Process"
          description="Apakah Anda yakin ingin menghapus data ini? Tindakan ini tidak dapat dibatalkan dan akan dicatat dalam log penghapusan."
          itemName={itemToDelete ? `${itemToDelete.request_number} - ${itemToDelete.penerima_nama} - Rp ${formatCurrency(itemToDelete.nilai_pengajuan)}` : ''}
          loading={deleteLoading}
        />
      </div>
    </Layout>
  );
}



